import { useMemo, useState } from "react"
import { max, mean, sum, uniqBy } from "lodash"

import { usePrimaryWarehouses } from "~/hooks/useWarehouses"

import DatePicker from "~/components/reusable/DatePicker"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import MiniTable from "~/components/reusable/miniTable/MiniTable"
import { addDays, differenceInDays } from "date-fns"
import { startOfTodayUTC } from "~/utils/datetime"
import { Checkbox } from "~/components/reusable/Checkbox"
import { Label } from "~/components/reusable/Label"
import {
  useCreateMultiplePurchaseOrders,
  useSuggestedPurchaseOrder,
} from "~/hooks/usePurchaseOrders"
import { roundToColliSize } from "~/components/purchaseOrder/CreateOrUpdatePurchaseOrder"
import { largest, groupBy } from "~/utils/tsUtils"
import { resolveVariantVendorId } from "~/utils/vendorUtils"
import { poGroupKey, poGroupNameDiscriminator, type PoGroupMeta } from "~/utils/poGranularity"
import { usePoGranularity } from "~/hooks/useCompany"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { formatNumber } from "~/utils/formatting"
import { WarehouseSelector } from "~/components/purchaseOrder/selectors/WarehouseSelector"
import { toast } from "react-toastify"
import { Warehouse } from "@phosphor-icons/react"
import { Card, CardContent } from "~/components/reusable/Card"
import { Separator } from "~/components/reusable/Separator"
import AdvancedDemandMapping from "./AdvancedDemandMapping"
import { formatName } from "~/utils/namingUtils"
import { api } from "~/utils/api"
import { Button } from "~/components/reusable/Button"
import { createPortal } from "react-dom"
import { useNavigate } from "@tanstack/react-router"

type POInputType = {
  expectedArrivalDate: Date
  nextOrderArrivalDate: Date
}

export function CreateMultiplePurchaseOrders({
  variants,
  start,
  end,
  preSelectedWarehouseId,
  collectionIds,
  onCreate,
  onClose,
}: {
  variants: IVariantSummary[]
  start?: Date
  end?: Date
  preSelectedWarehouseId?: string
  collectionIds?: string[]
  onCreate?: () => void
  onClose?: () => void
}) {
  const warehouses = usePrimaryWarehouses()
  const skuIds = variants.flatMap((v) => v.skuIds)
  const [demandMap, setDemandMap] = useState<Record<string, string[]>>({})
  const [syncPreorderQuantitiesMap, setSyncPreorderQuantitiesMap] = useState<
    Record<string, boolean>
  >({})
  const navigate = useNavigate()

  const selectedSkus = variants.flatMap((v) =>
    v.skuIds.map((skuId) => ({
      skuId,
      variantId: v.variantId,
      colliSize: v.settings.colliSize,
      totalLeadTimeDays: v.settings.totalLeadTimeDays,
      purchaseIntervalDays: v.settings.purchaseIntervalDays,
    })),
  )

  const allWarehouseIds = useMemo(() => warehouses?.map((wh) => wh.id) ?? [], [warehouses])

  const [POInput, setPOInput] = useState<POInputType>({
    expectedArrivalDate:
      start ??
      addDays(startOfTodayUTC(), max(selectedSkus.map((sku) => sku.totalLeadTimeDays)) ?? 0),
    nextOrderArrivalDate:
      end ??
      addDays(
        startOfTodayUTC(),
        (max(selectedSkus.map((sku) => sku.totalLeadTimeDays)) ?? 0) +
          (Math.ceil(mean(selectedSkus.map((sku) => sku.purchaseIntervalDays))) || 7),
      ),
  })

  const vendorMap = useMemo(() => {
    const uniqueVendors = uniqBy(
      variants.flatMap((v) => v.vendors),
      "id",
    )

    return new Map(uniqueVendors.map((vendor) => [vendor.id, vendor.name]))
  }, [variants])

  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(
    preSelectedWarehouseId ? [preSelectedWarehouseId] : allWarehouseIds,
  )

  const handleDemandMapChange = (newDemandMap: Record<string, string[]>) => {
    setDemandMap(newDemandMap)
  }

  const [onlySuggestedSkus, setOnlySuggestedSkus] = useState(false)
  const [datesModified, setDatesModified] = useState(false)

  const { data: preordersMap } = api.preOrder.getPreOrdersForPeriod.useQuery(
    {
      skuIds,
      warehouseIds:
        selectedWarehouseIds.length > 0
          ? [...selectedWarehouseIds, ...Object.values(demandMap).flat()]
          : allWarehouseIds,
      startDate: POInput.expectedArrivalDate,
      endDate: POInput.nextOrderArrivalDate,
    },
    { select: (data) => data as Map<string, Map<string, number>> },
  )

  const createMultiplePurchaseOrders = useCreateMultiplePurchaseOrders()
  const suggestedOrders = useSuggestedPurchaseOrder(
    selectedSkus.map((sku) => sku.skuId),
    selectedWarehouseIds.length > 0
      ? [...selectedWarehouseIds, ...Object.values(demandMap).flat()]
      : allWarehouseIds,
    datesModified ? POInput.expectedArrivalDate : undefined,
    datesModified ? POInput.nextOrderArrivalDate : undefined,
  )

  const mappedVariants = variants.map((v) => ({
    vendorId: resolveVariantVendorId(v),
    variantId: v.variantId,
    skuIds: v.skuIds,
  }))

  const groupedByVendor = groupBy(mappedVariants, "vendorId")

  const poGranularity = usePoGranularity("noos")

  const skuGroupMetaMap = useMemo(() => {
    const map = new Map<string, PoGroupMeta & { vendorName: string }>()
    for (const variant of variants) {
      const vendorId = resolveVariantVendorId(variant)
      const meta = {
        vendorId,
        vendorName: vendorMap.get(vendorId) ?? vendorId,
        productId: variant.productId,
        productName: variant.productName,
        variantId: variant.variantId,
        variantName: variant.variantName,
      }
      for (const skuId of variant.skuIds) map.set(skuId, meta)
    }
    return map
  }, [variants, vendorMap])

  const colliSizes = useMemo(() => {
    const colliSizeMap: Map<string, number> = new Map()
    selectedSkus.forEach((sku) => {
      if (sku.colliSize) {
        colliSizeMap.set(sku.skuId, sku.colliSize)
      }
    })
    return colliSizeMap
  }, [selectedSkus])

  const onSubmit = () => {
    if (suggestedOrders === undefined) {
      return
    }

    let items = suggestedOrders
    if (onlySuggestedSkus) {
      const skuToVariantMap = new Map<string, string>()
      variants.forEach((variant) => {
        variant.skuIds.forEach((skuId) => {
          skuToVariantMap.set(skuId, variant.variantId)
        })
      })

      const variantsWithSuggestions = new Set<string>()
      suggestedOrders.forEach((item) => {
        if (item.quantity > 0) {
          const variantId = skuToVariantMap.get(item.skuId)
          if (variantId) {
            variantsWithSuggestions.add(variantId)
          }
        }
      })

      items = suggestedOrders.filter((item) => {
        const variantId = skuToVariantMap.get(item.skuId)
        return variantId && variantsWithSuggestions.has(variantId)
      })
    }

    if (items.length === 0) {
      toast.warning("No items selected for purchase order")
      return
    }

    const warehouseAliasMap = new Map<string, string>()
    Object.entries(demandMap).forEach(([mainWarehouseId, subWarehouseIds]) => {
      for (const subWarehouseId of subWarehouseIds) {
        warehouseAliasMap.set(subWarehouseId, mainWarehouseId)
      }
    })

    // Step 1: Remap + Merge
    const mergedItemsMap = new Map<string, (typeof items)[0]>()

    items.forEach((item) => {
      const mergedWarehouseId = warehouseAliasMap.get(item.warehouseId) ?? item.warehouseId
      const key = `${mergedWarehouseId}_${item.skuId}`

      // If the warehouse is set to sync with preorders, override the quantity with the preorder quantity
      if (syncPreorderQuantitiesMap[mergedWarehouseId] && preordersMap) {
        const preorderQuantity = preordersMap.get(item.skuId)?.get(item.warehouseId) ?? 0
        item.quantity = preorderQuantity
        item.rawQuantity = preorderQuantity
      }

      if (!mergedItemsMap.has(key)) {
        mergedItemsMap.set(key, {
          ...item,
          warehouseId: mergedWarehouseId,
        })
      } else {
        const existing = mergedItemsMap.get(key)!
        mergedItemsMap.set(key, {
          ...existing,
          quantity: existing.quantity + item.quantity,
          rawQuantity: existing.rawQuantity + item.rawQuantity,
        })
      }
    })

    // Step 2: Group merged items by warehouse
    const mergedItems = Array.from(mergedItemsMap.values())
    const itemsByWarehouse = groupBy(mergedItems, "warehouseId")
    Object.entries(itemsByWarehouse).forEach(([warehouseId, warehouseItems]) => {
      const isSynced = syncPreorderQuantitiesMap[warehouseId]
      const itemsByGroup = groupBy(
        warehouseItems.filter((item) => skuGroupMetaMap.has(item.skuId)),
        (item) => poGroupKey(poGranularity, skuGroupMetaMap.get(item.skuId)!),
      )
      const warehouseGroups = Object.values(itemsByGroup)
        .map((groupItems) => {
          const meta = skuGroupMetaMap.get(groupItems[0]!.skuId)!
          return {
            vendor: { id: meta.vendorId, name: meta.vendorName },
            nameDiscriminator: poGroupNameDiscriminator(poGranularity, meta),
            items: groupItems.map((item) => ({
              skuId: item.skuId,
              warehouseId,
              quantity: isSynced
                ? Math.ceil(largest(item.rawQuantity, 0) ?? 0)
                : roundToColliSize(
                    Math.ceil(largest(item.rawQuantity, 0) ?? 0),
                    colliSizes.get(item.skuId) ?? 1,
                  ),
            })),
            addedDemandWarehouseIds: demandMap[warehouseId] ?? [],
          }
        })
        .filter((group) => group.items.length > 0)

      if (warehouseGroups.length > 0) {
        createMultiplePurchaseOrders.mutate(
          {
            groups: warehouseGroups,
            purchasePeriodEndDate: POInput.nextOrderArrivalDate,
            expectedArrivalDate: POInput.expectedArrivalDate,
            collectionIds: collectionIds,
          },
          {
            onSuccess: () => {
              onCreate?.()
              navigate({ to: "/procurement/purchase-orders" })
            },
          },
        )
      }
    })
  }

  const getSuggestedOrderQuantity = (skuIds: string[], warehouseIds: string[]) => {
    const skuSet = new Set(skuIds)
    const warehouseSet = new Set(warehouseIds)

    // If the warehouse is set to sync with preorders, return the preorder quantity
    if (warehouseIds[0] && syncPreorderQuantitiesMap[warehouseIds[0]] && preordersMap) {
      let quantityFromPreorders = 0

      for (const whId of warehouseIds) {
        for (const skuId of skuIds) {
          quantityFromPreorders += preordersMap.get(skuId)?.get(whId) ?? 0
        }
      }

      return quantityFromPreorders
    }

    // Otherwise, return the suggested order quantity
    let total = 0
    if (suggestedOrders) {
      for (const item of suggestedOrders) {
        if (skuSet.has(item.skuId) && warehouseSet.has(item.warehouseId)) {
          total += Math.ceil(Math.max(item.rawQuantity, 0))
        }
      }
    }

    return total
  }

  const handleSyncPreorderCheck = (warehouseId: string) => {
    const newValue = !syncPreorderQuantitiesMap[warehouseId]
    const newMap = { ...syncPreorderQuantitiesMap, [warehouseId]: newValue }
    setSyncPreorderQuantitiesMap(newMap)
  }

  const dialogFooterDomNode = document.getElementById("purchaseOrderDialogFooter")
  const endOfPurchasePeriodMinDate = addDays(POInput.expectedArrivalDate, 1)

  return (
    <div className="flex flex-col gap-4">
      <WarehouseSelector
        warehouses={warehouses}
        warehouseIds={selectedWarehouseIds}
        setWarehouseIds={setSelectedWarehouseIds}
        clearable
        isMulti
      />
      <AdvancedDemandMapping
        warehouseIds={selectedWarehouseIds}
        onDemandMapChange={handleDemandMapChange}
      />
      <div className="grid grid-cols-2 gap-4 mt-2">
        <div>
          <Label>Expected arrival date</Label>
          <DatePicker
            value={POInput.expectedArrivalDate}
            onChange={(date) => {
              if (!date) return
              setDatesModified(true)
              setPOInput({
                ...POInput,
                expectedArrivalDate: date,
                nextOrderArrivalDate: addDays(
                  POInput.nextOrderArrivalDate,
                  differenceInDays(date, POInput.expectedArrivalDate),
                ),
              })
            }}
            minDate={addDays(startOfTodayUTC(), 1)}
          />
        </div>
        <div>
          <div className="flex flex-row items-center gap-1">
            <Label>End of purchase period</Label>
            <InfoTooltip title="Length of the period you want your purchase order to cover." />
          </div>
          <DatePicker
            value={POInput.nextOrderArrivalDate}
            onChange={(date) => {
              if (!date) return
              setDatesModified(true)
              setPOInput({ ...POInput, nextOrderArrivalDate: date })
            }}
            minDate={endOfPurchasePeriodMinDate}
          />
        </div>
      </div>

      <Separator className="my-1" />

      <div className="flex flex-col gap-2">
        <p className="label-base">Suggested orders</p>
        <div className="flex flex-col gap-2 p-1">
          {warehouses && warehouses.length > 0 && (
            <div className="flex flex-col gap-3">
              {(selectedWarehouseIds.length === 0
                ? [...warehouses]
                : warehouses.filter((wh) => selectedWarehouseIds.includes(wh.id))
              )
                .sort((a, b) => a.id.localeCompare(b.id))
                .map((warehouse) => {
                  // Compute all warehouse IDs that map to this warehouse (including itself)
                  const mappedWarehouseIds = [warehouse.id, ...(demandMap[warehouse.id] ?? [])]

                  const warehouseItems =
                    syncPreorderQuantitiesMap[warehouse.id] && preordersMap
                      ? Array.from(preordersMap.entries()).flatMap(([skuId, skuMap]) => {
                          // Sum quantities for all mapped warehouse IDs
                          const totalQuantity = mappedWarehouseIds.reduce(
                            (sum, whId) => sum + (skuMap.get(whId) ?? 0),
                            0,
                          )
                          if (totalQuantity > 0) {
                            return [
                              {
                                skuId,
                                warehouseId: warehouse.id,
                                quantity: totalQuantity,
                                rawQuantity: totalQuantity,
                              },
                            ]
                          }
                          return []
                        })
                      : (suggestedOrders?.filter((item) =>
                          mappedWarehouseIds.includes(item.warehouseId),
                        ) ?? [])
                  const warehouseQuantity = sum(
                    warehouseItems.map((item) => Math.ceil(Math.max(item.rawQuantity, 0))),
                  )

                  const vendorsInWarehouse = Object.entries(groupedByVendor)
                    .map(([vendorId, vendorVariants]) => {
                      const vendorSkuIds = vendorVariants.flatMap((v) => v.skuIds)
                      const vendorWarehouseQuantity = getSuggestedOrderQuantity(vendorSkuIds, [
                        warehouse.id,
                        ...(demandMap[warehouse.id] ?? []),
                      ])
                      const variantIds = vendorVariants.map((v) => v.variantId)

                      return {
                        vendorId,
                        vendorName: vendorMap.get(vendorId) ?? vendorId,
                        skuIds: vendorSkuIds,
                        variantIds,
                        quantity: vendorWarehouseQuantity,
                      }
                    })
                    .sort((a, b) => a.vendorName.localeCompare(b.vendorName))

                  return (
                    <Card key={warehouse.id}>
                      <CardContent className="bg-subtle text-md p-1">
                        <div className="flex items-center justify-between p-2">
                          <div className="flex items-center gap-2">
                            <Warehouse />
                            <p className="paragraph-base">{warehouse.name}</p>
                            {demandMap[warehouse.id] && (
                              <span className="paragraph-xs text-fg-subtle">
                                with {demandMap[warehouse.id]?.length} additional warehouse(s)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`sync-preorder-quantity-${warehouse.id}`}
                              checked={!!syncPreorderQuantitiesMap[warehouse.id]}
                              onCheckedChange={() => handleSyncPreorderCheck(warehouse.id)}
                            />
                            <Label htmlFor={`sync-preorder-quantity-${warehouse.id}`}>
                              Sync to {formatName("preorder")} quantities
                            </Label>
                          </div>
                        </div>

                        <MiniTable
                          headers={["Vendor", "# Variants", "Quantity"]}
                          rows={vendorsInWarehouse.map((vendor) => [
                            vendor.vendorName,
                            formatNumber(vendor.variantIds.length, 0),
                            formatNumber(vendor.quantity, 0),
                          ])}
                        />

                        <div className="grid grid-cols-3 p-2">
                          <p className="label-base">Total</p>
                          <p className="label-base ml-[6px]">{formatNumber(variants.length, 0)}</p>
                          <p className="label-base ml-[12px]">
                            {formatNumber(warehouseQuantity, 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
            </div>
          )}
        </div>
        {!!dialogFooterDomNode &&
          createPortal(
            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="only-suggested-skus-checkbox"
                  checked={onlySuggestedSkus}
                  onCheckedChange={() => setOnlySuggestedSkus(!onlySuggestedSkus)}
                />
                <Label className="cursor-pointer" htmlFor="only-suggested-skus-checkbox">
                  Only include variants with recommendations
                </Label>
              </div>
              <div className="flex gap-2">
                <Button variant="transparent" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  requires="edit"
                  disabled={
                    !POInput.expectedArrivalDate ||
                    skuIds.length === 0 ||
                    (onlySuggestedSkus && !suggestedOrders?.some((s) => s.quantity > 0))
                  }
                  loading={createMultiplePurchaseOrders.isPending}
                  onClick={() => onSubmit()}
                >
                  Create Orders
                </Button>
              </div>
            </div>,
            dialogFooterDomNode,
          )}
      </div>
    </div>
  )
}
